// MACHINERY V2 (Stroje) — Špecifikácie tab chrome: glyphs, group divider, pill
// (M2.5).
//
// Split out of SpecSectionsV2.tsx to keep both files under the 300-line cap.
// This half is pure decoration — six group glyphs, a chevron, the count pill and
// the teal group header. The half next door renders DATA.
//
// THE SHEET'S TOOLBAR IS NOT HERE. `specSheetToolbar.tsx` owns it (M2.16d fix
// pass): the toolbar carries state-driven controls and two orders, which is a
// different job from these inert decorations, and folding it in put this file
// 52 lines over the cap.
//
// THE SIX GLYPHS REPLACE SIX MUI ICONS. v1 maps its group buckets to
// Tune / PrecisionManufacturing / Settings / Memory / Build / Extension
// (MachineSpecs.tsx `getGroupIcon`). v2 cannot import @mui/icons-material at
// all (§3), so each bucket gets a hand-drawn equivalent at the same 12px.
// Artboard 1006 draws a gear for ZÁKLADNÉ PARAMETRE and a control glyph for
// RIADENIE, which is what "core" and "control" render.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import { GroupKey } from "./specSheet";

export const Chevron: React.FC<{ open: boolean }> = ({ open }) => (
  <svg
    viewBox="0 0 12 12"
    width="12"
    height="12"
    fill="none"
    stroke="var(--muted)"
    strokeWidth="1.5"
    strokeLinecap="round"
    style={{ transform: open ? "rotate(180deg)" : "none", flexShrink: 0 }}
    aria-hidden
  >
    <path d="M3 4.5 L6 7.5 L9 4.5" />
  </svg>
);

const GroupGlyph: React.FC<{ groupKey: GroupKey }> = ({ groupKey }) => {
  const common = {
    viewBox: "0 0 14 14",
    width: 12,
    height: 12,
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.3,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  };
  switch (groupKey) {
    case "core":
      return (
        <svg {...common} aria-hidden>
          <line x1="2" y1="4" x2="12" y2="4" />
          <line x1="2" y1="10" x2="12" y2="10" />
          <circle cx="5.5" cy="4" r="1.6" />
          <circle cx="9" cy="10" r="1.6" />
        </svg>
      );
    case "spindle":
      return (
        <svg {...common} aria-hidden>
          <circle cx="7" cy="7" r="4.2" />
          <circle cx="7" cy="7" r="1.4" />
          <line x1="7" y1="1.2" x2="7" y2="2.6" />
        </svg>
      );
    case "workSupport":
      return (
        <svg {...common} aria-hidden>
          <rect x="2" y="5.5" width="10" height="3" rx="0.8" />
          <line x1="4" y1="8.5" x2="4" y2="11.5" />
          <line x1="10" y1="8.5" x2="10" y2="11.5" />
        </svg>
      );
    case "control":
      return (
        <svg {...common} aria-hidden>
          <rect x="3.2" y="3.2" width="7.6" height="7.6" rx="1.2" />
          <line x1="7" y1="1.2" x2="7" y2="3.2" />
          <line x1="7" y1="10.8" x2="7" y2="12.8" />
          <line x1="1.2" y1="7" x2="3.2" y2="7" />
          <line x1="10.8" y1="7" x2="12.8" y2="7" />
        </svg>
      );
    case "equipment":
      return (
        <svg {...common} aria-hidden>
          <path d="M9.4 2.6 a2.6 2.6 0 0 0 -3.4 3.4 L2.4 9.6 l2 2 l3.6 -3.6 a2.6 2.6 0 0 0 3.4 -3.4 L9.6 6.4 L7.6 4.4 Z" />
        </svg>
      );
    case "options":
      return (
        <svg {...common} aria-hidden>
          <line x1="7" y1="2.4" x2="7" y2="11.6" />
          <line x1="2.4" y1="7" x2="11.6" y2="7" />
        </svg>
      );
    default:
      return null;
  }
};

/** The teal group header that precedes the first section of a bucket. */
export const GroupDivider: React.FC<{ label: string; groupKey: GroupKey }> = ({
  label,
  groupKey,
}) => (
  <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 4, minWidth: 0 }}>
    <span
      style={{
        display: "inline-grid",
        placeItems: "center",
        width: 22,
        height: 22,
        borderRadius: 6,
        background: "var(--mod-accent-soft)",
        color: "var(--mod-accent)",
        border: "1px solid var(--mod-accent-line)",
        flexShrink: 0,
      }}
    >
      <GroupGlyph groupKey={groupKey} />
    </span>
    <span className="eyebrow" style={{ color: "var(--mod-accent)", whiteSpace: "nowrap" }}>
      {label}
    </span>
    <span style={{ flex: 1, height: 1, background: "var(--border-soft)" }} />
  </div>
);

export const CountPill: React.FC<{ label: string }> = ({ label }) => (
  <span
    style={{
      fontFamily: "var(--font-mono)",
      fontSize: 9,
      color: "var(--muted)",
      background: "var(--bg-soft)",
      border: "1px solid var(--border)",
      borderRadius: 999,
      padding: "1px 8px",
      whiteSpace: "nowrap",
      flexShrink: 0,
    }}
  >
    {label}
  </span>
);

/** i18n keys for the six group labels — the v2 copy of v1's `labelMap`. */
export const GROUP_LABEL_KEYS: Record<Exclude<GroupKey, null>, string> = {
  core: "myMachinery:specGroupCore",
  spindle: "myMachinery:specGroupSpindle",
  workSupport: "myMachinery:specGroupWorkSupport",
  control: "myMachinery:specGroupControl",
  equipment: "myMachinery:specGroupEquipment",
  options: "myMachinery:specGroupOptions",
};
